import React, { useMemo, useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import useIsMobile from '../hooks/useIsMobile';
import { getItemTypeColor } from '../utils/chartUtils';
import SummaryCard from './SummaryCard';
import ThroughputChartHeader from './ThroughputChartHeader';
import Filters from './Filters';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';

function groupByWeek(data) {
  // Agrupa por semana (YYYY-Www) e salva datas de início/fim
  const grouped = {};
  data.forEach(item => {
    const closedDate = new Date(item['Closed Date']);
    const year = closedDate.getUTCFullYear();
    const week = Math.ceil((closedDate - new Date(year, 0, 1)) / (1000 * 60 * 60 * 24 * 7));
    const weekStart = new Date(year, 0, 1 + (week - 1) * 7);
    const weekEnd = new Date(year, 0, 1 + week * 7 - 1);
    const key = `${year}-W${week.toString().padStart(2, '0')}`;
    if (!grouped[key]) grouped[key] = { week: key, weekStart, weekEnd };
    const type = item['Tipo de Item']?.trim() || 'Item';
    grouped[key][type] = (grouped[key][type] || 0) + 1;
  });
  return Object.values(grouped).sort((a, b) => a.week.localeCompare(b.week));
}

function formatWeekLabel(weekNum) {
  return `S${weekNum}`;
}

function ThroughputChart({ data, allTypes, visibleTypes, setVisibleTypes, plausible }) {
  const isMobile = useIsMobile();
  const chartAreaRef = useRef(null);

  // Filtro de datas
  const closedDates = useMemo(() => data.map(item => new Date(item['Closed Date'])).sort((a, b) => a - b), [data]);
  const defaultEnd = closedDates.length ? closedDates[closedDates.length - 1] : new Date();
  const defaultStart = closedDates.length ? new Date(defaultEnd) : new Date();
  if (closedDates.length) defaultStart.setUTCDate(defaultEnd.getUTCDate() - 7 * 7); // últimas 8 semanas
  const [startDate, setStartDate] = useState(defaultStart.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().slice(0, 10));

  // Filtro de escala
  const [scale, setScale] = useState('semanal');
  const scaleOptions = [
    { key: 'semanal', label: 'Semanal' },
    { key: 'quinzenal', label: 'Quinzenal' },
    { key: 'mensal', label: 'Mensal' },
  ];

  function groupByScale(data, scale) {
    if (scale === 'semanal') return groupByWeek(data);
    if (scale === 'quinzenal') return groupByQuinzena(data);
    if (scale === 'mensal') return groupByMes(data);
    return groupByWeek(data);
  }

  function groupByQuinzena(data) {
    const grouped = {};
    data.forEach(item => {
      const closedDate = new Date(item['Closed Date']);
      const year = closedDate.getUTCFullYear();
      const month = closedDate.getUTCMonth();
      const day = closedDate.getUTCDate();
      const quinzena = day <= 15 ? 1 : 2;
      const key = `${year}-${(month+1).toString().padStart(2, '0')}-Q${quinzena}`;
      if (!grouped[key]) grouped[key] = { periodo: key };
      const type = item['Tipo de Item']?.trim() || 'Item';
      grouped[key][type] = (grouped[key][type] || 0) + 1;
    });
    return Object.values(grouped).sort((a, b) => a.periodo.localeCompare(b.periodo));
  }

  function groupByMes(data) {
    const grouped = {};
    data.forEach(item => {
      const closedDate = new Date(item['Closed Date']);
      const year = closedDate.getUTCFullYear();
      const month = closedDate.getUTCMonth();
      const key = `${year}-${(month+1).toString().padStart(2, '0')}`;
      if (!grouped[key]) grouped[key] = { periodo: key };
      const type = item['Tipo de Item']?.trim() || 'Item';
      grouped[key][type] = (grouped[key][type] || 0) + 1;
    });
    return Object.values(grouped).sort((a, b) => a.periodo.localeCompare(b.periodo));
  }

  const filteredData = useMemo(() => data.filter(item => {
    const d = new Date(item['Closed Date']);
    const type = item['Tipo de Item']?.trim() || 'Item';
    return d >= new Date(startDate) && d <= new Date(endDate) && visibleTypes.includes(type);
  }), [data, startDate, endDate, visibleTypes]);

  const chartData = useMemo(() => groupByScale(filteredData, scale), [filteredData, scale]);

  // Função de exportação de imagem
  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    // Track PNG export
    if (plausible) {
      plausible.trackExport('png', 'throughput', 'throughput-chart');
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'throughput-chart.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  // Cálculo dos cards de resumo para throughput
  const total = chartData.reduce((acc, d) => acc + allTypes.reduce((sum, type) => sum + (d[type] || 0), 0), 0);
  const max = Math.max(...chartData.map(d => allTypes.reduce((sum, type) => sum + (d[type] || 0), 0)), 0);
  const min = Math.min(...chartData.map(d => allTypes.reduce((sum, type) => sum + (d[type] || 0), 0)).filter(v => v > 0), max);
  const avg = chartData.length > 0 ? Math.round(total / chartData.length) : 0;
  const summaryCards = [
    <SummaryCard key="total" label="Total de itens" value={total} colorClass="text-gray-800 text-lg" />, 
    <SummaryCard key="max" label="Throughput (máx)" value={max} colorClass="text-blue-700 text-lg" />, 
    <SummaryCard key="min" label="Throughput (mín)" value={min} colorClass="text-red-700 text-lg" />, 
    <SummaryCard key="avg" label="Throughput médio" value={avg} colorClass="text-green-700 text-lg" />,
  ];

  return (
    <>
      <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-16 px-0 sm:px-2 flex flex-col items-stretch relative">
        <ThroughputChartHeader onExport={handleExport} />
        <Filters
          allTypes={allTypes}
          visibleTypes={visibleTypes}
          setVisibleTypes={setVisibleTypes}
          visiblePercentiles={{}} // Throughput não tem percentis
          togglePercentile={() => {}} // Função vazia para throughput
        />
        <div className="flex flex-col sm:flex-row gap-2 items-center mb-2 w-full max-w-4xl mx-auto px-1">
          <div className="flex flex-row gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Data início:
              <input type="date" className="ml-2 border rounded px-2 py-1 text-sm" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} />
            </label>
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Data fim:
              <input type="date" className="ml-2 border rounded px-2 py-1 text-sm" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} />
            </label>
          </div>
          <div className="flex flex-row gap-2 w-full sm:w-auto justify-start sm:justify-end mt-2 sm:mt-0">
            {scaleOptions.map(opt => (
              <button
                key={opt.key}
                onClick={() => setScale(opt.key)}
                className={`px-3 py-1 rounded-full border text-sm font-medium transition-all ${scale === opt.key ? 'bg-blue-600 text-white border-blue-700 scale-105' : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50'}`}
                type="button"
                style={{ minWidth: 90 }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-row w-full flex-1 min-h-0 items-stretch">
          <div className="flex-1 min-w-0 flex flex-col h-full">
            <div ref={chartAreaRef} style={{ background: '#fff', borderRadius: 8, padding: 0 }}>
              <ResponsiveContainer width="100%" minWidth={120} minHeight={320}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 30, bottom: 30 }}>
                  <XAxis 
                    dataKey={scale === 'semanal' ? 'week' : 'periodo'}
                    tick={({ x, y, payload }) => {
                      let label = payload.value;
                      if (scale === 'semanal') {
                        const d = chartData.find(d => d.week === payload.value) || {};
                        if (d.weekStart && d.weekEnd) {
                          const pad = n => n.toString().padStart(2, '0');
                          label = `${pad(d.weekStart.getUTCDate())}/${pad(d.weekStart.getUTCMonth() + 1)}-${pad(d.weekEnd.getUTCDate())}/${pad(d.weekEnd.getUTCMonth() + 1)}`;
                        }
                      } else if (scale === 'quinzenal') {
                        // Formato: dd/MM - dd/MM
                        const d = chartData.find(d => d.periodo === payload.value) || {};
                        if (d.periodo) {
                          const [year, month, quin] = d.periodo.split(/-|Q/);
                          const start = new Date(year, parseInt(month, 10) - 1, quin === '1' ? 1 : 16);
                          const end = new Date(year, parseInt(month, 10) - 1, quin === '1' ? 15 : new Date(year, parseInt(month, 10), 0).getUTCDate());
                          const pad = n => n.toString().padStart(2, '0');
                          label = `${pad(start.getUTCDate())}/${pad(start.getUTCMonth() + 1)} - ${pad(end.getUTCDate())}/${pad(end.getUTCMonth() + 1)}`;
                        }
                      } else if (scale === 'mensal') {
                        // Formato: Mês/ano extenso
                        const d = chartData.find(d => d.periodo === payload.value) || {};
                        if (d.periodo) {
                          const [year, month] = d.periodo.split('-');
                          const date = new Date(year, parseInt(month, 10) - 1, 1);
                          // Usar a mesma formatação consistente com métodos UTC
                          const monthsUTC = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                                           'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
                          label = `${monthsUTC[date.getUTCMonth()]}/${date.getUTCFullYear()}`;
                          label = label.charAt(0).toUpperCase() + label.slice(1);
                        }
                      }
                      return (
                        <text x={x} y={y + (isMobile ? 8 : 12)} textAnchor="middle" fontSize={isMobile ? 10 : 13} fill="#374151" fontWeight={500}>
                          {label}
                        </text>
                      );
                    }}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }} label={{ value: 'Itens entregues', angle: -90, position: 'left', fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 600, dx: 0, dy: 0 }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: '#f3f4f6' }} />
                  {allTypes.filter(type => visibleTypes.includes(type)).map(type => (
                    <Bar key={type} dataKey={type} stackId="a" fill={getItemTypeColor(type, allTypes)} name={type} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row flex-nowrap w-full items-center gap-2 mt-6 max-w-4xl mx-auto pb-2 px-1 snap-x min-w-0 overflow-x-auto whitespace-nowrap">
        {summaryCards}
      </div>
    </>
  );
}

export default ThroughputChart;
